// One-tap logging with Undo and optional follow-up details in the toast.

import { deleteEvent, endSleep, endTimed, logEvent, openDuration, startSleep, startTimed, updateEvent } from '../../data/actions.ts';
import { data } from '../../data/index.ts';
import { DEFAULT_HOUSEHOLD } from '../../data/seed.ts';
import { formatDuration } from '../../domain/time.ts';
import type { LogEvent } from '../../domain/types.ts';
import { EVENT_META } from '../../app/eventMeta.ts';
import { toast } from '../../ui/toast.tsx';
import { openEditor } from './EventEditor.tsx';

function undoable(ev: LogEvent, message: string, extra: { label: string; onClick: () => void; keep?: boolean }[] = [], detail?: string) {
  toast({
    message,
    detail,
    icon: EVENT_META[ev.type].icon,
    tone: 'good',
    actions: [...extra, { label: 'Edit', onClick: () => openEditor(data().store.get('events', ev.id) ?? ev) }, { label: 'Undo', onClick: () => deleteEvent(ev.id) }],
  });
}

function toiletCue() {
  return data().store.get('settings', 'household')?.toiletCue ?? DEFAULT_HOUSEHOLD.toiletCue;
}

export function quickWee() {
  const ev = logEvent('wee', { where: 'outside' });
  undoable(ev, 'Wee outside — good boy!', [], 'Reward within a couple of seconds, every time.');
}

export function quickPoo() {
  const ev = logEvent('poo', { where: 'outside' });
  const setQ = (quality: 'firm' | 'soft' | 'runny') => () => {
    const cur = data().store.get('events', ev.id);
    if (cur && cur.type === 'poo') updateEvent({ ...cur, data: { ...cur.data, quality } });
  };
  undoable(ev, 'Poo outside — brilliant!', [
    { label: 'Firm', onClick: setQ('firm') },
    { label: 'Soft', onClick: setQ('soft') },
    { label: 'Runny', onClick: setQ('runny') },
  ]);
}

export function quickAccident(kind: 'wee' | 'poo') {
  const ev = logEvent(kind, { where: 'inside' });
  toast({
    message: `Accident logged`,
    detail: 'No telling off — clean with enzyme cleaner and take him out more often. It’s data, not failure.',
    icon: EVENT_META[kind].icon,
    tone: 'warn',
    actions: [
      { label: 'Edit', onClick: () => openEditor(data().store.get('events', ev.id) ?? ev) },
      { label: 'Undo', onClick: () => deleteEvent(ev.id) },
    ],
    duration: 9000,
  });
}

export function quickMeal(grams?: number) {
  const ev = logEvent('meal', { grams, appetite: 'all' });
  const setA = (appetite: 'most' | 'some' | 'none') => () => {
    const cur = data().store.get('events', ev.id);
    if (cur && cur.type === 'meal') updateEvent({ ...cur, data: { ...cur.data, appetite } });
  };
  undoable(
    ev,
    grams ? `Meal logged · ${grams} g` : 'Meal logged',
    [
      { label: 'Ate most', onClick: setA('most') },
      { label: 'Some', onClick: setA('some') },
      { label: 'Refused', onClick: setA('none') },
    ],
    `Toilet trip in ~15 minutes. “${toiletCue()}”.`,
  );
}

export function toggleSleep() {
  const open = openDuration('sleep');
  if (open) {
    const ended = endSleep();
    if (ended?.end) {
      toast({
        message: `Awake after ${formatDuration(ended.end - ended.at)}`,
        detail: `Straight outside now — “${toiletCue()}”.`,
        icon: EVENT_META.sleep.icon,
        tone: 'accent',
        actions: [
          { label: 'Wee outside', primary: true, onClick: quickWee },
          { label: 'Undo', onClick: () => updateEvent({ ...ended, end: undefined } as LogEvent) },
        ],
        duration: 12_000,
      });
    }
  } else {
    const ev = startSleep();
    undoable(ev, 'Nap started', [], 'Sleep is when puppies grow and process what they’ve learnt. Let him be.');
  }
}

export function toggleTimed(type: 'walk' | 'play' | 'alone') {
  const open = openDuration(type);
  const label = { walk: 'Outing', play: 'Play', alone: 'Alone time' }[type];
  if (open) {
    const ended = endTimed(type);
    if (ended?.end)
      toast({
        message: `${label} finished · ${formatDuration(ended.end - ended.at)}`,
        icon: EVENT_META[type].icon,
        tone: 'good',
        actions: [
          { label: 'Edit', onClick: () => openEditor(data().store.get('events', ended.id) ?? ended) },
          { label: 'Undo', onClick: () => updateEvent({ ...ended, end: undefined } as LogEvent) },
        ],
      });
  } else {
    const ev = startTimed(type);
    undoable(ev, `${label} started`, [], type === 'alone' ? 'Calm, boring exit. Come back before he gets upset.' : undefined);
  }
}

export function quickSimple(type: 'water' | 'treat') {
  const ev = logEvent(type, type === 'treat' ? {} : ({} as Record<string, never>));
  undoable(ev, `${EVENT_META[type].label} logged`);
}
